import React from 'react';
import { Box, Chip, Paper, Typography } from '@mui/material';
import { AttendanceStatus, AttendanceSummary, CourseSession } from '../../services/attendanceService';

export const STATUS_META: Record<AttendanceStatus, { label: string; short: string; color: 'success' | 'error' | 'warning' | 'info' }> = {
  present: { label: 'Présent·e', short: 'P', color: 'success' },
  late: { label: 'En retard', short: 'R', color: 'warning' },
  absent: { label: 'Absent·e', short: 'A', color: 'error' },
  excused: { label: 'Absence justifiée', short: 'J', color: 'info' },
};

export const STATUS_ORDER: AttendanceStatus[] = ['present', 'late', 'absent', 'excused'];

const WEEKDAYS = ['dim.', 'lun.', 'mar.', 'mer.', 'jeu.', 'ven.', 'sam.'];

// "mar. 18/11/2026" — la date ISO (AAAA-MM-JJ) est lue en heure locale, pas en UTC
export function formatSessionDate(isoDate: string): string {
  const [y, m, d] = isoDate.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  return `${WEEKDAYS[date.getDay()]} ${String(d).padStart(2, '0')}/${String(m).padStart(2, '0')}/${y}`;
}

export function formatSessionTime(session: CourseSession): string {
  if (!session.start_time) return '';
  return session.end_time ? `${session.start_time}–${session.end_time}` : session.start_time;
}

export function sessionTitle(session: CourseSession): string {
  const label = session.label && session.label !== session.session_type ? `${session.session_type} · ${session.label}` : session.session_type;
  return `${label} — ${session.td_group || 'Promo'}`;
}

export function teacherNames(session: CourseSession): string {
  if (session.teachers.length === 0) return session.teachers_label || '—';
  return session.teachers.map(t => `${t.firstname || ''} ${t.lastname || ''}`.trim()).join(', ');
}

export const StatusChip: React.FC<{ status: AttendanceStatus | null | undefined; isPast?: boolean; compact?: boolean }> = ({ status, isPast = true, compact = false }) => {
  if (status) {
    const meta = STATUS_META[status];
    return <Chip size="small" color={meta.color} label={compact ? meta.short : meta.label} title={meta.label} />;
  }
  if (!isPast) return <Chip size="small" variant="outlined" label={compact ? '·' : 'À venir'} title="À venir" />;
  return <Chip size="small" variant="outlined" label={compact ? '?' : 'Non saisi'} title="Non saisi" />;
};

// Part des séances passées où l'étudiant·e était là (présent·e ou en retard), sur les séances saisies
export function attendanceRate(summary: AttendanceSummary): number | null {
  const recorded = summary.present + summary.late + summary.absent + summary.excused;
  return recorded ? Math.round(((summary.present + summary.late) / recorded) * 100) : null;
}

export const SummaryTiles: React.FC<{ summary: AttendanceSummary }> = ({ summary }) => {
  const rate = attendanceRate(summary);
  const tiles = [
    { label: 'Séances passées', value: summary.past },
    { label: 'Présences', value: summary.present },
    { label: 'Retards', value: summary.late },
    { label: 'Absences', value: summary.absent },
    { label: 'Absences justifiées', value: summary.excused },
    { label: 'Taux de présence', value: rate === null ? '—' : `${rate} %` },
  ];
  return (
    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
      {tiles.map(tile => (
        <Paper key={tile.label} variant="outlined" sx={{ px: 2, py: 1.5, minWidth: 130 }}>
          <Typography variant="body2" color="text.secondary">{tile.label}</Typography>
          <Typography variant="h5">{tile.value}</Typography>
        </Paper>
      ))}
    </Box>
  );
};
